import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { ChannelApi } from '../../api';
import { Spinner } from '../../components';
import { useProfile } from '../../hooks';
import type { Channel, ChannelPaymentMethod, ChannelPaymentMethods } from '../../model';
import { ChannelCard } from './ui';

export const ChannelsPage = () => {
  const queryClient = useQueryClient();
  const profile = useProfile();

  const canReadChannels = !!profile?.permissions.includes('channel.read');
  const canEditChannels = !!profile?.permissions.includes('channel.edit');

  const { data: channels } = useQuery({
    queryKey: [ChannelApi.KEY, 'getChannels'],
    queryFn: ChannelApi.getChannels,
    enabled: canReadChannels,
  });

  // список методов запрашивается только после того, как получены каналы
  const { data: paymentMethods = {} } = useQuery({
    queryKey: [ChannelApi.KEY, 'getPaymentMethods'],
    queryFn: ChannelApi.getPaymentMethods,
    enabled: !!channels,
  });

  const toggleChannelMutation = useMutation({
    mutationFn: async (channel: Channel) => {
      await ChannelApi.toggleChannel(channel.id);

      return ChannelApi.getChannelById(channel.id);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<Channel[]>([ChannelApi.KEY, 'getChannels'], (current) =>
        (current ?? []).map((channel) => (channel.id === updated.id ? updated : channel)),
      );
    },
    onError: () => {
      toast.error('Failed to switch channel');
    },
  });

  const togglePaymentMethodMutation = useMutation({
    mutationFn: async ({ paymentMethod }: { channelId: string; paymentMethod: ChannelPaymentMethod }) => {
      await ChannelApi.togglePaymentMethod(paymentMethod.id);

      return ChannelApi.getPaymentMethodById(paymentMethod.id);
    },
    onSuccess: (updated, { channelId }) => {
      queryClient.setQueryData<ChannelPaymentMethods>(
        [ChannelApi.KEY, 'getPaymentMethods'],
        (current) => ({
          ...current,
          [channelId]: (current?.[channelId] ?? []).map((paymentMethod) =>
            paymentMethod.id === updated.id ? updated : paymentMethod,
          ),
        }),
      );
    },
    onError: () => {
      toast.error('Failed to switch payment method');
    },
  });

  const handleToggleChannel = useCallback(
    (channel: Channel) => toggleChannelMutation.mutate(channel),
    [toggleChannelMutation],
  );

  if (!canReadChannels) {
    return null;
  }

  if (!channels) {
    return <Spinner />;
  }

  return (
    <>
      <h1 className="mb-4 text-4xl text-brand-6 font-bold">Channels</h1>
      <div className="p-7 bg-white rounded-3xl">
        <ul className="grid gap-6">
          {channels.map((channel) => (
            <ChannelCard
              canEdit={canEditChannels}
              channel={channel}
              isChannelPending={
                toggleChannelMutation.isPending &&
                toggleChannelMutation.variables?.id === channel.id
              }
              key={channel.id}
              onToggleChannel={handleToggleChannel}
              onTogglePaymentMethod={(paymentMethod) =>
                togglePaymentMethodMutation.mutate({
                  channelId: String(channel.id),
                  paymentMethod,
                })
              }
              paymentMethods={
                Array.isArray(paymentMethods[String(channel.id)])
                  ? paymentMethods[String(channel.id)]
                  : []
              }
              pendingPaymentMethodId={
                togglePaymentMethodMutation.isPending
                  ? togglePaymentMethodMutation.variables?.paymentMethod.id
                  : undefined
              }
            />
          ))}
          {channels.length === 0 && <li className="text-brand-2">Nothing found</li>}
        </ul>
      </div>
    </>
  );
};
